import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Offer } from '../../model/offer';
import { UpgradeAvailabilityDTO } from '../../model/upgradeAvailabilityDTO';

import { UiNewShellComponent, UiNewStyle, UiNewTheme } from '../_ui-new/ui-new-shell/ui-new-shell.component';
import { UiNewTopBarComponent } from '../_ui-new/ui-new-top-bar/ui-new-top-bar.component';
import { UiNewTabsBarComponent, UiNewViewMode } from '../_ui-new/ui-new-tabs-bar/ui-new-tabs-bar.component';
import { UiNewOfferTab } from '../_ui-new/ui-new-offer-nav-bar/ui-new-offer-nav-bar.component';
import { UiNewViewBarComponent } from '../_ui-new/ui-new-view-bar/ui-new-view-bar.component';
import { UiNewOfferCardListResponsiveComponent } from '../_ui-new/ui-new-offer-card-list-responsive/ui-new-offer-card-list-responsive.component';
import { ThemeService } from '../../_services/theme.service';
import { ThemePlaygroundComponent } from '../theme-playground/theme-playground.component';
import { LxResponsiveClassDirective } from '../../directives/lx-responsive-class.directive';
import {ThemeControlsComponent} from "@app/components/theme-controls/theme-controls.component";
import { AuthService } from '../../_services/auth.service';
import { Router } from '@angular/router';
import { OfferCategoryService } from '../../_services/offer-category.service';
import { OfferCategory } from '../../model/offer-category.model';

interface ThemeConfigData {
  upgradeAvailability: UpgradeAvailabilityDTO;
  offerExpires: { ts_expires: string };
  guestInfo: {
    guest_fname: string;
    guest_lname: string;
    has_guest_email: boolean;
  };
  applicationConfig?: any;
  offers: {
    room: Offer[];
    eci: Offer[];
    lco: Offer[];
    addon: Offer[];
  };
}

@Component({
  selector: 'app-theme-config',
  standalone: true,
  imports: [
    CommonModule,
    UiNewShellComponent,
    UiNewTopBarComponent,
    UiNewTabsBarComponent,
    UiNewViewBarComponent,
    ThemePlaygroundComponent,
    LxResponsiveClassDirective,
    ThemeControlsComponent,
  ],
  templateUrl: './theme-config.component.html',
  styleUrl: './theme-config.component.css',
})
export class ThemeConfigComponent implements OnInit {
  uiNewOfferListComponent = UiNewOfferCardListResponsiveComponent;

  readonly pageResponsiveClass = {
    horizontal: 'page-wrap-desktop',
    vertical: 'page-wrap-handset',
  } as const;

  theme: UiNewTheme = 'dark';
  style: UiNewStyle = 'neo';
  logoUrl: string | null = null;

  offers: Offer[] = [];
  offers$: Observable<Offer[]> = of([]);
  offerCount: number | null = null;

  loadingPage = false;
  themeReady = true;
  requestOnlyMode = false;
  isAuthenticated = false;

  upgradeAvailability: UpgradeAvailabilityDTO = {
    room_available: false,
    eci_available: false,
    lco_available: false,
    addon_available: false,
  };

  selectedTab: UiNewOfferTab = 'ROOM';
  viewMode: UiNewViewMode = 'tiles';

  endDate: Date = new Date();

  private themeConfigData: ThemeConfigData | null = null;

  constructor(
    private http: HttpClient,
    private themeService: ThemeService,
    private authService: AuthService,
    private router: Router,
    private offerCategoryService: OfferCategoryService,
  ) {}

  ngOnInit(): void {
    this.validateAuthToken();
  }

  private validateAuthToken(): void {
    this.authService.validateTokenFromCookie().subscribe({
      next: (result) => {
        if (result.success) {
          this.isAuthenticated = true;
          this.loadThemeConfigData();
        } else {
          this.router.navigate(['/unauthorized']);
        }
      },
      error: () => {
        this.router.navigate(['/unauthorized']);
      }
    });
  }

  private loadThemeConfigData(): void {
    this.http.get<ThemeConfigData>('/assets/theme-config-sample-data.json')
      .subscribe({
        next: (data) => {
          this.themeConfigData = data;
          this.upgradeAvailability = data.upgradeAvailability;

          if (data.offerExpires?.ts_expires) {
            this.endDate = new Date(data.offerExpires.ts_expires);
          }

          // Apply theme from JSON if available
          if (data.applicationConfig?.prestay?.theme) {
            try {
              this.themeService.setThemeOrFallback(data.applicationConfig.prestay.theme, 'zen');
            } catch {
              // ignore
            }
          }

          // Set default tab to first available
          this.setDefaultTab(this.upgradeAvailability);
        },
        error: (err) => {
          console.error('Failed to load theme config data:', err);
        }
      });
  }

  onTabChanged(tab: UiNewOfferTab): void {
    this.selectedTab = tab;
    this.loadOffersForTab(tab);
  }

  private loadOffersForTab(tab: UiNewOfferTab): void {
    if (!this.themeConfigData) {
      this.offers$ = of([]);
      return;
    }

    const tabOffersByCategory: Record<OfferCategory, Offer[]> = {
      ROOM: this.themeConfigData.offers.room || [],
      ECI: this.themeConfigData.offers.eci || [],
      LCO: this.themeConfigData.offers.lco || [],
      ANCILLARY: this.themeConfigData.offers.addon || [],
    };
    const tabOffers: Offer[] = tabOffersByCategory[tab] || [];

    this.offers = tabOffers;
    this.offers$ = of(tabOffers);
    this.offerCount = tabOffers.length;
  }

  private setDefaultTab(upgradeAvailability: UpgradeAvailabilityDTO): void {
    const firstAvailableKey = this.getFirstAvailableKey(upgradeAvailability);
    const tab = (firstAvailableKey ?? 'ROOM') as UiNewOfferTab;
    this.onTabChanged(tab);
  }

  private getFirstAvailableKey(upgrade: UpgradeAvailabilityDTO): UiNewOfferTab | null {
    return this.offerCategoryService.getFirstAvailableCategory(upgrade);
  }

  onViewModeChange(mode: UiNewViewMode): void {
    this.viewMode = mode;
  }

  onRefresh(): void {
    // Reload the current tab
    this.loadOffersForTab(this.selectedTab);
  }

  // Stub methods for child component bindings (no-op for display-only mode)
  onLogoRendered(): void {}
  onNoThanks(): void {}
  onViewMore(): void {}
}
